/*
 * BSTBDY Bundles / Stacks collection page.
 * Two behaviours, both scoped to a section root so they survive theme-editor
 * section reloads:
 *   1. Client-side price-band filter (the bundles are hand-picked blocks, so
 *      there is no Shopify product query to facet).
 *   2. Scroll-reveal via IntersectionObserver, with a no-support fallback and a
 *      2.5s safety timeout so a failed observer never leaves the page blank.
 * All motion is skipped under prefers-reduced-motion.
 */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- Reveal */
  function initReveal(root) {
    if (!root || root.dataset.bundlesRevealInit === 'true') return;
    var els = Array.prototype.slice.call(root.querySelectorAll('[data-reveal]'));
    if (!els.length) return;
    root.dataset.bundlesRevealInit = 'true';

    // Reduced motion: CSS already keeps everything visible — do nothing.
    if (REDUCE) return;

    var show = function (el) {
      el.classList.remove('bundles-reveal--hidden');
    };

    els.forEach(function (el, i) {
      var d = (i % 4) * 0.07;
      el.classList.add('bundles-reveal--hidden');
      // Delay only opacity/transform; keep border-color hover snappy.
      el.style.transition =
        'opacity .7s cubic-bezier(.2,.7,.2,1) ' + d + 's, ' +
        'transform .7s cubic-bezier(.2,.7,.2,1) ' + d + 's, ' +
        'border-color .3s ease';
    });

    // No IntersectionObserver: reveal everything immediately.
    if (!('IntersectionObserver' in window)) {
      els.forEach(show);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          show(e.target);
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    els.forEach(function (el) { io.observe(el); });

    // Safety net: reveal all after 2.5s regardless of the observer.
    setTimeout(function () { els.forEach(show); }, 2500);
  }

  /* ---------------------------------------------------------------- Filter */
  function initFilter(root) {
    if (!root) return;
    var wrap = root.querySelector('[data-bundles-filter]');
    if (!wrap || wrap.dataset.bundlesFilterInit === 'true') return;
    wrap.dataset.bundlesFilterInit = 'true';

    var chips = Array.prototype.slice.call(wrap.querySelectorAll('.bundles-chip'));
    // Cards carry data-band too, but so do the chips — exclude anything inside the filter.
    var cards = Array.prototype.slice
      .call(root.querySelectorAll('[data-band]'))
      .filter(function (el) { return !el.closest('[data-bundles-filter]'); });
    var nextdrop = root.querySelector('[data-bundles-nextdrop]');
    var live = root.querySelector('[data-bundles-live]');
    if (!chips.length) return;

    var validBands = chips.map(function (c) { return c.dataset.band; });

    function apply(band) {
      if (validBands.indexOf(band) === -1) band = 'all';

      chips.forEach(function (chip) {
        chip.setAttribute('aria-pressed', chip.dataset.band === band ? 'true' : 'false');
      });

      var count = 0;
      cards.forEach(function (card) {
        var match = band === 'all' || card.dataset.band === band;
        card.hidden = !match;
        if (match) count += 1;
      });

      // The "next drop" tile only makes sense in the unfiltered view.
      if (nextdrop) nextdrop.hidden = band !== 'all';

      if (live) {
        live.textContent =
          count + (count === 1 ? ' stack' : ' stacks') +
          (band === 'all' ? ' shown' : ' shown in this price range');
      }

      // Keep the filter shareable via ?band=… without stacking history.
      try {
        var url = new URL(window.location.href);
        if (band === 'all') {
          url.searchParams.delete('band');
        } else {
          url.searchParams.set('band', band);
        }
        window.history.replaceState({}, '', url);
      } catch (e) { /* older browsers: skip URL sync */ }
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () { apply(chip.dataset.band); });
    });

    // Restore from the URL on load.
    var initial = 'all';
    try {
      initial = new URL(window.location.href).searchParams.get('band') || 'all';
    } catch (e) { /* noop */ }
    apply(initial);
  }

  /* ------------------------------------------------------------------ Boot */
  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-bundles-section]');
    Array.prototype.forEach.call(roots, function (root) {
      initReveal(root);
      initFilter(root);
    });
  }

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  }

  // Theme editor: re-init when a section is loaded / reordered.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
